import { isOpenPartnerProductOrderItemsSaveModal } from "@/atoms/item/is-open-partner-product-order-items-save-modal";
import { selectedPartnerProductOrderCartItemsState } from "@/atoms/item/selected-partner-product-order-cart-items-state";
import { previewState } from "@/atoms/quote/preview-state";
import { profileState } from "@/atoms/user/profile-state";
import { DEBOUNCE_TIME } from "@/common/constant/debounce-time";
import { ButtonColor } from "@/common/enum/button-color";
import { PartnerOrderPath } from "@/common/enum/path";

import {
  formatCommasNumber,
  formatNumber,
} from "@/common/util/format-number-with-commas";
import { quoteService } from "@/services/quote-service";
import { ItemDetailLineDto } from "@/types/dto/item-detail-line-dto";
import { Button } from "flowbite-react";
import { debounce } from "lodash";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { Divider } from "../devider/Devider";
import { Body16Strong } from "../typography/Body16Strong";
import { Footnote12Description } from "../typography/Footnote12Description";

// 발주 진행 버튼 있음

export const PartnerProductOrderCartFooter = () => {
  const navigate = useNavigate();
  const cartItems = useRecoilValue(selectedPartnerProductOrderCartItemsState);
  const setSaveCartItems = useSetRecoilState(
    isOpenPartnerProductOrderItemsSaveModal
  );
  const [preview, setPreview] = useRecoilState(previewState);
  const profile = useRecoilValue(profileState);
  const isOpen = !!preview;

  const getPreview = async (cartItems: ItemDetailLineDto[]) => {
    try {
      const payload = cartItems.map((line) => ({
        itemId: line.childItem.id,
        quantity: line.quantity,
      }));

      const response = await quoteService.preview(payload);
      if (!response.ok) {
        setPreview(undefined);
        return;
      }
      setPreview(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  const getPreviewDebounce = useCallback(
    debounce(async (cartItems) => {
      await getPreview(cartItems);
    }, DEBOUNCE_TIME),
    []
  );

  useEffect(() => {
    getPreviewDebounce(cartItems);
  }, [cartItems]);

  const handleSaveButtonClick = () => {
    setSaveCartItems(cartItems);
  };

  const onClickButton = () => {
    if (!profile || !preview) {
      return;
    }

    const updatedPreview = {
      ...preview,
      cartItems: preview?.cartItems.map((item, i) => ({
        ...item,
        memoItems: cartItems[i]?.childItem?.memoItems || [],
      })),
      canEdit: true,
    };
    setPreview(updatedPreview);
    navigate(PartnerOrderPath.CREATE_ORDER);
  };

  return (
    <div className="px-[16px] py-[16px] flex flex-col gap-[8px] items-center justify-between shadow-[0px_-2px_14.1px_0px_#0000000F] border-t-[1px] border-semantic-divider">
      {isOpen && (
        <div className="flex flex-col mb-[16px] w-full">
          <div className="flex justify-between py-[4px]">
            <Footnote12Description className="text-semantic-text-secondary">
              정가
            </Footnote12Description>
            <Footnote12Description className="text-semantic-text-body">
              {formatCommasNumber(preview.originalPriceSum)}원
            </Footnote12Description>
          </div>
          <div className="flex justify-between py-[4px]">
            <Footnote12Description className="text-semantic-text-secondary">
              기본 할인
            </Footnote12Description>
            <Footnote12Description className="text-semantic-text-body">
              {formatNumber(preview.discountPriceSum, true)}원
            </Footnote12Description>
          </div>
          <div className="flex justify-between py-[4px] text-secondary-2-red">
            <Footnote12Description>인센티브 할인</Footnote12Description>
            <Footnote12Description>
              {formatNumber(preview.incentiveDiscountPriceSum, true)}원
            </Footnote12Description>
          </div>
          <div className="flex justify-between py-[4px]">
            <Footnote12Description className="text-semantic-text-secondary">
              배송비
            </Footnote12Description>
            <Footnote12Description className="text-semantic-text-body">
              {preview.shippingPrice === 0
                ? "무료"
                : `${formatCommasNumber(preview.shippingPrice)}원`}
            </Footnote12Description>
          </div>
          <Divider />
          <div className="flex justify-between py-[12px] text-semantic-text-heading">
            <Body16Strong>총 금액 (VAT 포함)</Body16Strong>
            <Body16Strong>
              {formatCommasNumber(preview.finalPriceSum)}원
            </Body16Strong>
          </div>
        </div>
      )}
      <div className="w-full flex gap-[8px]">
        <Button
          className="w-full"
          size="xl"
          color={isOpen ? ButtonColor.TRANSPARENT_OUTLINE : ""}
          onClick={handleSaveButtonClick}
          disabled={!isOpen}
        >
          견적서 저장
        </Button>

        <Button
          className="w-full"
          size="xl"
          color={isOpen ? ButtonColor.PRIMARY : ""}
          disabled={!isOpen}
          onClick={onClickButton}
        >
          발주 진행
        </Button>
      </div>
    </div>
  );
};
